import React, { useEffect, useRef, useState } from "react";
import { IoMdMore } from "react-icons/io";
import { LuFilter } from "react-icons/lu";
import {
  FaArrowDown,
  FaArrowUp,
  FaTrash,
  FaSpinner,
  FaSave,
  FaTimes,
  FaEdit,
} from "react-icons/fa";
import { ArrowUpDown } from "lucide-react";
import { HiPencil } from "react-icons/hi2";
import { FaTrainSubway } from "react-icons/fa6";
import api from "../../services/AxiosConnection";
import useAuth from "../../hooks/useAuth";

const formatTrainId = (trainId) => {
  if (!trainId) return "N/A";
  const parts = trainId.split("_");
  if (parts.length !== 7) return trainId;
  const [year, month, day, hour, minute] = parts;
  return `${day}/${month}/${year}\u00A0\u00A0\u00A0${hour}:${minute}`;
};

const TrainLogList = ({
  trainLogList,
  onLogClick,
  highlightedTrainId,
  fetchTrainLogs,
  rightColumnHeight,
}) => {
  const { user, selectedSite } = useAuth();
  const canEdit = user?.role === "Super Admin" || user?.role === "Admin";
  const selectedLogRef = useRef(null);
  const filterIconRef = useRef(null);
  const sortIconRef = useRef(null);
  const filterDropdownRef = useRef(null);
  const sortDropdownRef = useRef(null);
  const popupRef = useRef(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [trainNumberInput, setTrainNumberInput] = useState("");
  const [trainNameInput, setTrainNameInput] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [hasExistingData, setHasExistingData] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [selectedFilter, setSelectedFilter] = useState("All");
  const [sortOption, setSortOption] = useState("newest");

  useEffect(() => {
    let isMounted = true;

    if (selectedLogRef.current && isMounted) {
      selectedLogRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }

    return () => {
      isMounted = false;
    };
  }, [highlightedTrainId, trainLogList]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        filterDropdownRef.current &&
        !filterDropdownRef.current.contains(event.target) &&
        filterIconRef.current &&
        !filterIconRef.current.contains(event.target)
      ) {
        setIsFilterOpen(false);
      }
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target) &&
        sortIconRef.current &&
        !sortIconRef.current.contains(event.target)
      ) {
        setIsSortOpen(false);
      }
      if (
        popupRef.current &&
        !popupRef.current.contains(event.target) &&
        isPopupOpen
      ) {
        handleCancel();
      }
    };

    if (isFilterOpen || isSortOpen || isPopupOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterOpen, isSortOpen, isPopupOpen]);

  useEffect(() => {
    if (isPopupOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isPopupOpen]);

  const getTrainNumber = (trainNumber) => {
    if (trainNumber === null || trainNumber === "Train Number") return null;
    return trainNumber;
  };

  const getTrainName = (trainName) => {
    if (trainName === null || trainName === "Train Name") return null;
    return trainName;
  };

  const handleEditClick = (log) => {
    if (!canEdit) return;
    setSelectedLog(log);
    setTrainNumberInput(getTrainNumber(log.TrainNumber) || "");
    setTrainNameInput(getTrainName(log.TrainName) || "");
    setIsPopupOpen(true);
    setIsEditMode(false);
    const hasData =
      getTrainNumber(log.TrainNumber) || getTrainName(log.TrainName);
    setHasExistingData(!!hasData);
  };

  const handleSave = async () => {
    if (!canEdit) return;
    setIsSaving(true);
    try {
      const payload = {
        siteDb: selectedSite,
        trainId: selectedLog.TrainId,
        trainNumber: trainNumberInput || null,
        trainName: trainNameInput || null,
      };

      await api.put("/simple-view/updateTrainDetails", payload);
      fetchTrainLogs();
      handleCancel();
    } catch (error) {
      console.error("Error saving data:", error);
      alert("Failed to save train details.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!canEdit) return;
    setIsSaving(true);
    try {
      const payload = {
        siteDb: selectedSite,
        trainId: selectedLog.TrainId,
      };
      await api.put("/simple-view/updateTrainDetails", payload);
      fetchTrainLogs();
      handleCancel();
    } catch (error) {
      console.error("Error deleting data:", error);
      alert("Failed to delete train details.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (!canEdit) return;
    setIsPopupOpen(false);
    setSelectedLog(null);
    setTrainNumberInput("");
    setTrainNameInput("");
    setIsEditMode(false);
    setHasExistingData(false);
    setIsSaving(false);
  };

  const toggleFilterDropdown = () => {
    setIsFilterOpen(!isFilterOpen);
    setIsSortOpen(false);
  };

  const toggleSortDropdown = () => {
    setIsSortOpen(!isSortOpen);
    setIsFilterOpen(false);
  };

  const handleFilterSelect = (filter) => {
    setSelectedFilter(filter);
    setIsFilterOpen(false);
  };

  const handleSortSelect = (option) => {
    setSortOption(option);
    setIsSortOpen(false);
  };

  const filteredTrainLogs = trainLogList.filter((log) => {
    if (selectedFilter === "All") return true;
    if (selectedFilter === "Trains with Alarms") return log.hasAlarm === 1;
    if (selectedFilter === "Trains with Warnings") return log.hasWarning === 1;
    return false;
  });

  const sortedTrainLogs = [...filteredTrainLogs].sort((a, b) => {
    const dateA = new Date(a.DateofCreation);
    const dateB = new Date(b.DateofCreation);
    return sortOption === "newest" ? dateB - dateA : dateA - dateB;
  });

  const headerHeight = 60;
  const selectTrainLogHeight = 210;
  const trainLogListHeight =
    rightColumnHeight > 0
      ? rightColumnHeight - selectTrainLogHeight - headerHeight
      : "auto";

  return (
    <div className="bg-[#e6e7eb] rounded-xl md:min-h-[600px] flex flex-col shadow-sm">
      <div className="flex flex-col p-4">
        <div className="flex justify-between items-center">
          <h3 className="font-bold text-black">Train Log List</h3>
          <div className="flex items-center gap-4">
            <div className="relative" ref={filterIconRef}>
              <LuFilter
                className="h-5 w-5 cursor-pointer text-gray-700 hover:text-blue-900 transition-colors"
                onClick={toggleFilterDropdown}
                title="Filter Trains"
              />
              {isFilterOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-56 bg-white border border-blue-200 rounded-xl shadow-lg z-10 overflow-hidden"
                  ref={filterDropdownRef}
                >
                  <ul className="py-2 text-sm text-gray-800">
                    <li
                      className={`px-4 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-900 transition-colors ${
                        selectedFilter === "All"
                          ? "bg-blue-100 text-blue-900 font-semibold"
                          : ""
                      }`}
                      onClick={() => handleFilterSelect("All")}
                    >
                      All
                    </li>
                    <li
                      className={`px-4 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-900 transition-colors ${
                        selectedFilter === "Trains with Alarms"
                          ? "bg-blue-100 text-blue-900 font-semibold"
                          : ""
                      }`}
                      onClick={() => handleFilterSelect("Trains with Alarms")}
                    >
                      Trains with Alarms
                    </li>
                    <li
                      className={`px-4 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-900 transition-colors ${
                        selectedFilter === "Trains with Warnings"
                          ? "bg-blue-100 text-blue-900 font-semibold"
                          : ""
                      }`}
                      onClick={() => handleFilterSelect("Trains with Warnings")}
                    >
                      Trains with Warnings
                    </li>
                  </ul>
                </div>
              )}
            </div>
            <div className="relative" ref={sortIconRef}>
              <ArrowUpDown
                className="h-5 w-5 cursor-pointer text-gray-700 hover:text-blue-900 transition-colors"
                onClick={toggleSortDropdown}
                title="Sort Trains"
              />
              {isSortOpen && (
                <div
                  className="absolute right-0 top-full mt-2 w-56 bg-white border border-blue-200 rounded-xl shadow-lg z-10 overflow-hidden"
                  ref={sortDropdownRef}
                >
                  <ul className="py-2 text-sm text-gray-800">
                    <li
                      className={`px-4 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-900 transition-colors ${
                        sortOption === "newest"
                          ? "bg-blue-100 text-blue-900 font-semibold"
                          : ""
                      }`}
                      onClick={() => handleSortSelect("newest")}
                    >
                      Newest First
                    </li>
                    <li
                      className={`px-4 py-2 cursor-pointer hover:bg-blue-50 hover:text-blue-900 transition-colors ${
                        sortOption === "oldest"
                          ? "bg-blue-100 text-blue-900 font-semibold"
                          : ""
                      }`}
                      onClick={() => handleSortSelect("oldest")}
                    >
                      Oldest First
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
        <span className="text-sm text-gray-600 mt-1">
          Total Trains: {filteredTrainLogs.length}
        </span>
      </div>

      <div
        className="space-y-1 overflow-y-auto scrollbar-custom max-h-64 md:max-h-none"
        style={{ height: trainLogListHeight }}
      >
        {sortedTrainLogs.length === 0 ? (
          <div className="flex justify-center items-center p-4 bg-[#e6e7eb] text-gray-700 text-sm">
            No Train Logs Available
          </div>
        ) : (
          sortedTrainLogs.map((log, index) => {
            const hasAlarm = log.hasAlarm;
            const hasWarning = log.hasWarning;
            const isSelected = log.TrainId === highlightedTrainId;
            const isActualTrain = log.Actual_Train;
            const directionText =
              log.Direction === "UP"
                ? "U"
                : log.Direction === "DOWN"
                ? "D"
                : "ND";
            
            // Updated train icon color logic - always shows red/yellow if has alarm/warning
            const trainIconColor = hasAlarm 
            ? "text-red-600"  // Always red if alarm exists (even if selected)
            : hasWarning 
              ? "text-yellow-400"  // Yellow if warning exists (and no alarm)
              : isSelected 
                ? "text-white"  // White if selected with no alerts
                : "text-gray-600";  // Gray if not selected and no alerts

            return (
              <div
                key={log.TrainId}
                ref={isSelected ? selectedLogRef : null}
                className={`flex items-stretch justify-start px-4 py-2 cursor-pointer ${
                  isSelected
                    ? "bg-blue-900 text-white"
                    : isActualTrain
                    ? "bg-white hover:bg-gray-200"
                    : "bg-yellow-100 hover:bg-yellow-200"
                }`}
                onClick={() => onLogClick(log.TrainId)}
              >
                {/* Train Icon spanning all 4 rows */}
                <div className="flex items-center pr-4">
                  <FaTrainSubway className={`text-xl ${trainIconColor}`} />
                </div>
                <div className="flex flex-col flex-1 justify-between">
                  {/* Line 1: Index and Icons */}
                  <div className="flex items-center justify-between">
                    <span
                      className={`text-xs font-bold ${
                        isSelected ? "text-white" : "text-black"
                      }`}
                    >
                      #{index + 1}
                    </span>
                    {canEdit && (
                      <div className="flex items-center gap-2">
                        <HiPencil
                          className={`h-3 w-3 cursor-pointer ${
                            isSelected ? "text-white" : "text-gray-600"
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditClick(log);
                          }}
                        />
                        <IoMdMore
                          className={`h-3 w-3 ${
                            isSelected ? "text-white" : "text-gray-600"
                          }`}
                        />
                      </div>
                    )}
                  </div>
                  {/* Line 2: Train Date/Time and Direction */}
                  <div className="flex items-center justify-between mt-1">
                    <span
                      className={`text-xs ${
                        isSelected ? "text-white" : "text-gray-700"
                      }`}
                    >
                      {formatTrainId(log.TrainId)}
                    </span>
                    <div className="flex items-center gap-1">
                      {log.Direction === "DOWN" ? (
                        <FaArrowDown
                          className={`text-base ${
                            isSelected ? "text-white" : "text-gray-600"
                          }`}
                        />
                      ) : log.Direction === "UP" ? (
                        <FaArrowUp
                          className={`text-base ${
                            isSelected ? "text-white" : "text-gray-600"
                          }`}
                        />
                      ) : null}
                      <span
                        className={`text-xs ${
                          isSelected ? "text-white" : "text-gray-700"
                        }`}
                      >
                        {directionText}
                      </span>
                    </div>
                  </div>
                  {/* Line 3: Train Name | Train Number */}
                  {(getTrainName(log.TrainName) ||
                    getTrainNumber(log.TrainNumber)) && (
                    <div
                      className={`text-xs mt-1 ${
                        isSelected ? "text-white" : "text-gray-700"
                      }`}
                    >
                      {getTrainName(log.TrainName) ||
                      getTrainNumber(log.TrainNumber)
                        ? `${getTrainName(log.TrainName) || ""}${
                            getTrainName(log.TrainName) &&
                            getTrainNumber(log.TrainNumber)
                              ? "\u00A0\u00A0\u00A0|\u00A0\u00A0\u00A0"
                              : ""
                          }${getTrainNumber(log.TrainNumber) || ""}`
                        : ""}
                    </div>
                  )}
                  {/* Line 4: Alarm and Warning Counts */}
                  <div
                    className={`text-xs mt-1 ${
                      isSelected ? "text-white" : "text-gray-700"
                    }`}
                  >
                    <span className={hasAlarm ? "text-red-600" : isSelected ? "text-white" : "text-gray-700"}>
                      Alarm
                    </span>
                    : {log.AlarmCount}
                       |   
                    <span
                      className={hasWarning ? "text-yellow-400" : isSelected ? "text-white" : "text-gray-700"}
                    >
                      Warning
                    </span>
                    : {log.WarningCount}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {isPopupOpen && canEdit && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
          <div
            ref={popupRef}
            className="bg-white p-6 rounded-lg shadow-lg w-96"
          >
            <h3 className="text-lg font-bold mb-4">
              {hasExistingData ? "Edit Train Details" : "Add Train Details"}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Train Number
                </label>
                <input
                  type="text"
                  value={trainNumberInput}
                  onChange={(e) => setTrainNumberInput(e.target.value)}
                  className="mt-1 p-2 border rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={hasExistingData && !isEditMode}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Train Name
                </label>
                <input
                  type="text"
                  value={trainNameInput}
                  onChange={(e) => setTrainNameInput(e.target.value)}
                  className="mt-1 p-2 border rounded-md w-full focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={hasExistingData && !isEditMode}
                />
              </div>
              <div className="flex justify-end gap-2">
                {hasExistingData && !isEditMode ? (
                  <>
                    <button
                      onClick={() => setIsEditMode(true)}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-800 text-white font-medium rounded-lg transition text-sm flex items-center gap-1"
                    >
                      <FaEdit /> Edit
                    </button>
                    <button
                      onClick={handleDelete}
                      className="px-4 py-2 bg-red-600 hover:bg-red-800 text-white font-medium rounded-lg transition text-sm flex items-center gap-1"
                      disabled={isSaving}
                    >
                      <FaTrash /> Delete
                      {isSaving && <FaSpinner className="animate-spin" />}
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium rounded-lg transition text-sm flex items-center gap-1"
                    >
                      <FaTimes /> Close
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-gray-800 font-medium rounded-lg transition text-sm flex items-center gap-1"
                      disabled={isSaving}
                    >
                      <FaTimes /> Cancel
                    </button>
                    <button
                      onClick={handleSave}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-800 text-white font-medium rounded-lg transition text-sm flex items-center gap-1"
                      disabled={isSaving}
                    >
                      <FaSave /> Save
                      {isSaving && <FaSpinner className="animate-spin" />}
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrainLogList;